import React, { useState, useRef, useEffect } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt, FaClock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import lv from 'date-fns/locale/lv';
import ru from 'date-fns/locale/ru';
import enGB from 'date-fns/locale/en-GB';
import './UnifiedDateTimePicker.css';

// Register locales for react-datepicker
registerLocale('lv', lv);
registerLocale('ru', ru);
registerLocale('en', enGB);

/**
 * Unified Date/Time Picker Component
 * Provides consistent date and time selection across the application
 * Supports different styling themes for different use cases
 */

// Date Picker Component
export function UnifiedDatePicker({
  selected,
  onChange,
  disabled = false,
  theme = 'default', // 'default', 'filter'
  placeholder = 'Select date',
  minDate = new Date(),
  className = '',
  isClearable = false,
  selectsStart = false,
  selectsEnd = false,
  startDate = null,
  endDate = null,
  ...props
}) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);
  const datePickerRef = useRef(null);

  // Get current locale for calendar
  const getLocale = () => {
    const lang = i18n.language;
    if (lang === 'lv') return 'lv';
    if (lang === 'ru') return 'ru';
    return 'en';
  };

  const handleCalendarOpen = () => {
    setIsOpen(true);
    if (pickerRef.current) {
      pickerRef.current.classList.add('calendar-open');
      // Add class to document body to prevent global transforms
      document.body.classList.add('calendar-is-open');
    }
  };

  const handleCalendarClose = () => {
    setIsOpen(false);
    if (pickerRef.current) {
      pickerRef.current.classList.remove('calendar-open');
      // Remove class from document body
      document.body.classList.remove('calendar-is-open');
    }
  };

  // Handle input focus to open calendar and blur to prevent keyboard
  const handleInputFocus = (e) => {
    e.target.blur(); // Immediately blur to prevent keyboard
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true); // Open the calendar
    }
  };

  return (
    <div 
      ref={pickerRef}
      className={`unified-date-picker-wrapper ${theme} ${disabled ? 'disabled' : ''} ${className}`}
    >
      <div className="picker-icon">
      </div>
      <DatePicker
        ref={datePickerRef}
        selected={selected}
        onChange={onChange}
        dateFormat="dd/MM/yyyy"
        className={`unified-date-picker-input ${theme}`}
        minDate={minDate}
        disabled={disabled}
        placeholderText={placeholder}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        isClearable={isClearable}
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        startDate={startDate}
        endDate={endDate}
        locale={getLocale()}
        calendarStartDay={1}
        onFocus={handleInputFocus}
        popperProps={{
          strategy: 'absolute',
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
              },
            },
          ],
        }}
        {...props}
      />
    </div>
  );
}

// Time Picker Component
export function UnifiedTimePicker({
  selected,
  onChange,
  disabled = false,
  theme = 'default', // 'default', 'filter'
  placeholder = 'Select time',
  timeIntervals = 15,
  className = '',
  isClearable = false,
  ...props
}) {
  const { t } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);
  const timePickerRef = useRef(null);

  const handleCalendarOpen = () => {
    setIsOpen(true);
    if (pickerRef.current) {
      pickerRef.current.classList.add('calendar-open');
      // Add class to document body to prevent global transforms
      document.body.classList.add('calendar-is-open');
    }
  };

  const handleCalendarClose = () => {
    setIsOpen(false);
    if (pickerRef.current) {
      pickerRef.current.classList.remove('calendar-open');
      // Remove class from document body
      document.body.classList.remove('calendar-is-open');
    }
  };

  // Handle input focus to open time picker and blur to prevent keyboard
  const handleInputFocus = (e) => {
    e.target.blur(); // Immediately blur to prevent keyboard
    if (timePickerRef.current) {
      timePickerRef.current.setOpen(true); // Open the time picker
    }
  };

  return (
    <div 
      ref={pickerRef}
      className={`unified-time-picker-wrapper ${theme} ${disabled ? 'disabled' : ''} ${className}`}
    >
      <div className="picker-icon">
      </div>
      <DatePicker
        ref={timePickerRef}
        selected={selected}
        onChange={onChange}
        showTimeSelect
        showTimeSelectOnly
        timeFormat="HH:mm"
        timeIntervals={timeIntervals}
        timeCaption={t('filters.time')}
        dateFormat="HH:mm"
        className={`unified-time-picker-input ${theme}`}
        disabled={disabled}
        placeholderText={placeholder}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        isClearable={isClearable}
        onFocus={handleInputFocus}
        popperProps={{
          strategy: 'absolute',
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
              },
            },
          ],
        }}
        {...props}
      />
    </div>
  );
}

// Combined Date Time Picker (for PublishPost and SendPackage)
export function UnifiedDateTimePicker({ 
  dateValue, 
  timeValue, 
  onDateChange, 
  onTimeChange, 
  disabled = false,
  theme = 'default',
  className = ''
}) {
  return (
    <div className={`unified-datetime-container ${theme} ${className}`}>
      <UnifiedDatePicker
        selected={dateValue}
        onChange={onDateChange}
        disabled={disabled}
        theme={theme}
        placeholder="Select date"
      />
      <UnifiedTimePicker
        selected={timeValue}
        onChange={onTimeChange}
        disabled={disabled}
        theme={theme}
        placeholder="Select time"
      />
    </div>
  );
}

// Date Range Picker (for Home filters)
export function UnifiedDateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  theme = 'filter',
  className = ''
}) {
  const { t } = useTranslation('common');

  const handleStartDateChange = (date) => {
    onStartDateChange(date);
    // If end date exists and is before new start date, clear it
    if (endDate && date && endDate < date) {
      onEndDateChange(null);
    }
  };

  const handleEndDateChange = (date) => {
    // Only allow end date if it's after start date
    if (startDate && date && date < startDate) {
      return; // Don't update if invalid
    }
    onEndDateChange(date);
  };

  return (
    <div className={`unified-date-range-container ${theme} ${className}`}>
      <UnifiedDatePicker
        selected={startDate}
        onChange={handleStartDateChange}
        selectsStart
        startDate={startDate}
        endDate={endDate}
        theme={theme}
        placeholder={t('filters.start_date')}
        minDate={null} // Allow past dates for filters
        isClearable={true}
      />
      <UnifiedDatePicker
        selected={endDate}
        onChange={handleEndDateChange}
        selectsEnd
        startDate={startDate}
        endDate={endDate}
        minDate={startDate}
        theme={theme}
        placeholder={t('filters.end_date')}
        isClearable={true}
      />
    </div>
  );
}

// Time Range Picker (for Home filters)
export function UnifiedTimeRangePicker({
  startTime,
  endTime,
  onStartTimeChange,
  onEndTimeChange,
  theme = 'filter',
  className = ''
}) {
  const { t } = useTranslation('common');

  const handleStartTimeChange = (time) => {
    onStartTimeChange(time);
    // If end time exists and is before new start time, clear it
    if (endTime && time && endTime < time) {
      onEndTimeChange(null);
    }
  };

  const handleEndTimeChange = (time) => {
    // Only allow end time if it's after start time
    if (startTime && time && time < startTime) {
      return; // Don't update if invalid
    }
    onEndTimeChange(time);
  };

  return (
    <div className={`unified-time-range-container ${theme} ${className}`}>
      <UnifiedTimePicker
        selected={startTime}
        onChange={handleStartTimeChange}
        theme={theme}
        placeholder={t('filters.start_time')}
        isClearable={true}
      />
      <UnifiedTimePicker
        selected={endTime}
        onChange={handleEndTimeChange}
        theme={theme}
        placeholder={t('filters.end_time')}
        isClearable={true}
      />
    </div>
  );
}

export default UnifiedDateTimePicker;
